// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useState } from 'react';
import axios from 'axios';
import { Button, TextArea, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../../api/proposalApi';
import type { BreakthroughTaxSectionContent, ProposalSection } from '../../../../../types/proposal';
import { formatCurrency } from '../../../proposalDisplay';

export type SectionTaxProps = Readonly<{
  proposalId: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Renders the "Client view" preview (BR-P5, PRD §5 / RQ-BR8): the notes
   * table is already read-only and renders unchanged; the commentary editor
   * + Save button are replaced with a plain disabled display of whatever has
   * been saved. Defaults to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): BreakthroughTaxSectionContent {
  return (section.content as BreakthroughTaxSectionContent | null) ?? {};
}

/**
 * BR_TAX (PRD §4 row 10) — server-derived capital-position/tax context notes
 * (net worth, assets/liabilities, estimated current-year tax) plus the
 * advisor's free-text commentary.
 */
export function SectionTax({ proposalId, section, onSaved, readOnly = false }: SectionTaxProps) {
  const { showToast } = useToast();
  const content = readContent(section);
  const notes = content.notes ?? [];
  const [commentary, setCommentary] = useState(content.commentary ?? '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await proposalApi.updateSection(proposalId, section.sectionKey, {
        content: { ...content, commentary },
      });
      setDirty(false);
      onSaved(updated);
      showToast('Tax saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Tax'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, proposalId, section.sectionKey, content, commentary, onSaved, showToast]);

  return (
    <div className="pension-builder__section" data-testid="section-br-tax">
      {notes.length > 0 ? (
        <table className="pension-builder__table" data-testid="section-br-tax-notes">
          <thead>
            <tr>
              <th>Item</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((n, i) => (
              <tr key={`${n.label ?? 'note'}-${i}`}>
                <td>{n.label ?? '—'}</td>
                <td>{formatCurrency(n.value, n.currencyCode) ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="pension-builder__empty" data-testid="section-br-tax-empty">
          No tax context available from this relationship's Financial Position yet.
        </p>
      )}

      {readOnly ? (
        commentary && (
          <TextArea
            label="Commentary"
            value={commentary}
            disabled
            readOnly
            rows={5}
            fluid
            data-testid="section-br-tax-commentary"
          />
        )
      ) : (
        <>
          <TextArea
            label="Commentary"
            value={commentary}
            onChange={(e) => {
              setCommentary(e.target.value);
              setDirty(true);
            }}
            placeholder="Tax considerations relevant to the client…"
            rows={5}
            fluid
            data-testid="section-br-tax-commentary"
          />

          <div className="pension-builder__section-footer">
            <Button
              variant="filled"
              onClick={handleSave}
              disabled={saving || !dirty}
              loading={saving}
              data-testid="section-br-tax-save-btn"
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default SectionTax;
